/**
 * The RFP's amenity and policy questions — one canonical list, in Supplier
 * Relations' own wording.
 *
 * From Jeff's field list, 2026-09-10 ("this is what we'd need for the pop-outs"),
 * checked against the eleven keys already in `Agreement.amenities`. See feedback
 * log item 70 for the line-by-line comparison.
 *
 * ── Why a module and not a form ────────────────────────────────────────────
 * Two surfaces ask these questions — the create form and the drawer's edit
 * modal — and a third reads them back (the drawer's Amenities group). Three
 * copies of a question list drift, and the way they drift is the worst kind:
 * silently, one key at a time, until an answer typed on one screen has nowhere
 * to land on another. `CONTACT_REASONS` was split out for exactly this reason.
 *
 * ── Three keys are RESHAPED, and the old spellings still have to READ ──────
 * `Agreement.amenities` is free-form jsonb and the only thing that has ever
 * written it is the synthetic seed, so this is the cheapest moment this reshape
 * will ever be. But the old answers must still render, so `fromAnswers` coerces
 * the legacy shapes rather than dropping them:
 *
 *   `breakfast_included: true`   →  a BOOLEAN where the ask is C/B/F/N.
 *                                   Replaced by `breakfast_type`.
 *   `parking_included: 'Waived…'`→  ONE string doing three jobs: what kind of
 *                                   parking, what it costs, and whether one car
 *                                   is in the rate. Replaced by `parking_type`,
 *                                   `parking_cost`, `parking_one_car_included`.
 *   `resort_fee_included: null`  →  already three-state, now spelled yes/no/na
 *   `planned_renovations`            so an unanswered question is distinct from
 *   `rates_available_to_contractors` a "no" — which a boolean cannot express.
 *
 * ── What is NOT here ───────────────────────────────────────────────────────
 * `restaurant_on_site`, `full_service` and `virtual_cards_accepted` exist in the
 * seed's key set but are absent from Jeff's list. They are left out rather than
 * quietly carried: the drawer still renders any key it finds, so old answers
 * keep displaying, and adding a question back is one line here. Flagged to Jeff.
 *
 * Rate availability (LRA/NLRA/Dual Load) and rate type (fixed/dynamic/both) are
 * also on his list and deliberately NOT here — they belong to a rate ROW, not
 * to the agreement, and `agreement_rate` has no write path yet. Item 70.
 */

/** Three-state, because "nobody has answered" is not "no". */
export const YES_NO_NA = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'na', label: 'N/A' },
] as const;

/** Codes as the sheet writes them, expanded so nobody has to remember them. */
export const BREAKFAST_TYPES = [
  { value: 'C', label: 'C — Continental' },
  { value: 'B', label: 'B — Buffet' },
  { value: 'F', label: 'F — Full American' },
  { value: 'N', label: 'N — Not included' },
] as const;

export const PARKING_TYPES = [
  { value: 'lot', label: 'Parking lot' },
  { value: 'garage', label: 'Parking garage' },
  { value: 'valet', label: 'Valet' },
  { value: 'street', label: 'Street side' },
  { value: 'public', label: 'Public' },
  { value: 'none', label: 'None available' },
] as const;

export type AmenityControl = 'select' | 'text' | 'textarea';

export interface AmenityQuestion {
  /** The `amenities` jsonb key this answer is stored under. */
  key: string;
  /** The question as asked. Long on purpose — these are the hotel's words. */
  label: string;
  control: AmenityControl;
  options?: readonly { value: string; label: string }[];
  /** Placeholder, for the ones where the shape of the answer is not obvious. */
  placeholder?: string;
  /**
   * Only asked when another answer has a particular value — "if yes, please
   * provide dates", "if no, include fee details". Asking unconditionally makes
   * the modal longer and invites a note explaining a "no" that was never given.
   */
  showWhen?: { key: string; equals: string };
}

export const AMENITY_QUESTIONS: readonly AmenityQuestion[] = [
  {
    key: 'gds_loading_confirmed',
    label: 'Confirm willing and able to submit rate loading for the GDS',
    control: 'select',
    options: YES_NO_NA,
  },
  {
    key: 'rates_available_to_contractors',
    label:
      'Are client negotiated rates available to client-employed contractors and consultants with proper identification?',
    control: 'select',
    options: YES_NO_NA,
  },
  {
    key: 'breakfast_type',
    label: 'Type of breakfast included in the negotiated rate',
    control: 'select',
    options: BREAKFAST_TYPES,
  },
  {
    key: 'parking_type',
    label: 'Parking available',
    control: 'select',
    options: PARKING_TYPES,
  },
  {
    key: 'parking_cost',
    label: 'Parking cost',
    control: 'text',
    placeholder: 'Standard costs, if any — e.g. $24/night self-park',
  },
  {
    key: 'parking_one_car_included',
    label: 'Parking for one car included in the negotiated rate',
    control: 'select',
    options: YES_NO_NA,
  },
  {
    key: 'resort_fee_included',
    label: 'Do the rates include any and all resort fees, service fees, etc.?',
    control: 'select',
    options: YES_NO_NA,
  },
  {
    key: 'resort_fee_details',
    label: 'Fee details',
    control: 'textarea',
    placeholder: 'Which fees are charged, and how much',
    // Only when the answer is no — that is the case the question exists for.
    showWhen: { key: 'resort_fee_included', equals: 'no' },
  },
  {
    key: 'fnb_discount',
    label: 'On-site food and beverage discount',
    control: 'text',
    placeholder: 'Percentage discount, if offered — e.g. 15%',
  },
  {
    key: 'fnb_discount_restrictions',
    label: 'F&B discount restrictions',
    control: 'textarea',
    placeholder: 'Outlets it applies to, alcohol, room service, blackout times',
  },
  {
    key: 'planned_renovations',
    label:
      'Does the property have any renovations planned or currently occurring?',
    control: 'select',
    options: YES_NO_NA,
  },
  {
    key: 'planned_renovations_details',
    label: 'Renovation dates and specifics',
    control: 'textarea',
    placeholder: 'Dates, floors or facilities affected, and expected impact',
    showWhen: { key: 'planned_renovations', equals: 'yes' },
  },
  {
    key: 'cancellation_policy',
    label: 'Cancellation policy',
    control: 'text',
    // The ask, stated. Jeff: "Cancellation Policy - requesting 24 hours".
    placeholder: 'We ask for 24 hours',
  },
];

/** Every answer, keyed. Strings throughout: a form holds text. */
export type AmenityAnswers = Record<string, string>;

export function blankAnswers(): AmenityAnswers {
  return Object.fromEntries(AMENITY_QUESTIONS.map((q) => [q.key, '']));
}

/**
 * Is this question asked, given what else has been answered?
 *
 * A hidden question's answer is still carried in state — clearing it on hide
 * would lose a paragraph of renovation detail to a mis-click on the yes/no
 * above it — but `toPayload` drops it, so a stale detail note cannot be stored
 * against an answer that no longer implies it.
 */
export function isAsked(q: AmenityQuestion, answers: AmenityAnswers): boolean {
  if (!q.showWhen) return true;
  return answers[q.showWhen.key] === q.showWhen.equals;
}

/** How many of the questions actually asked have an answer. */
export function answeredCount(answers: AmenityAnswers): number {
  return AMENITY_QUESTIONS.filter(
    (q) => isAsked(q, answers) && (answers[q.key] ?? '').trim() !== '',
  ).length;
}

/** How many questions are on screen right now, for "3 of 11 answered". */
export function askedCount(answers: AmenityAnswers): number {
  return AMENITY_QUESTIONS.filter((q) => isAsked(q, answers)).length;
}

/**
 * The `amenities` value to send, or undefined when nothing was answered.
 *
 * UNDEFINED, NOT `{}`. An empty object is a recorded answer set with no answers
 * in it, which would overwrite whatever a previous edit stored; undefined omits
 * the key so the API leaves the column alone. That distinction is the same one
 * the status control makes about `reason: ''`.
 *
 * Blank answers are dropped rather than stored as '', and an answer to a
 * question that is no longer asked is dropped with them.
 */
export function toPayload(
  answers: AmenityAnswers,
): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const q of AMENITY_QUESTIONS) {
    if (!isAsked(q, answers)) continue;
    const v = (answers[q.key] ?? '').trim();
    if (v !== '') out[q.key] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Read a stored blob back into form answers.
 *
 * COERCES THE LEGACY SHAPES. Three keys were booleans (or null) before this
 * list existed, and `<select value={true}>` renders as unanswered — so a stored
 * `true` would silently present as "not answered yet" and the next save would
 * erase it. See the module header for the three.
 */
export function fromPayload(
  stored: Record<string, unknown> | null | undefined,
): AmenityAnswers {
  const answers = blankAnswers();
  if (!stored) return answers;

  for (const q of AMENITY_QUESTIONS) {
    const raw = stored[q.key];
    if (raw === undefined || raw === null) continue;
    answers[q.key] = coerce(raw, q);
  }

  // The boolean breakfast flag predates the C/B/F/N ask and cannot be mapped
  // onto it: `true` says breakfast is included but not which kind, and
  // guessing "Continental" would invent an answer the hotel never gave. So it
  // maps only in the direction that loses nothing.
  if (answers.breakfast_type === '' && stored.breakfast_included === false) {
    answers.breakfast_type = 'N';
  }

  // The old conflated parking string is the COST answer in every real example
  // ('Waived for contracted rate'), and it is prose, so it goes to the free
  // text field rather than being matched against the six options.
  if (
    answers.parking_cost === '' &&
    typeof stored.parking_included === 'string'
  ) {
    answers.parking_cost = stored.parking_included;
  }

  return answers;
}

function coerce(raw: unknown, q: AmenityQuestion): string {
  if (typeof raw === 'boolean') {
    // Only meaningful for the yes/no/na questions; a boolean in a text field
    // still reads better as the word than as "true".
    return raw ? 'yes' : 'no';
  }
  const s = String(raw).trim();
  if (q.options && !q.options.some((o) => o.value === s)) {
    /*
     * A value outside the option list. Dropped rather than shown, because a
     * `<select>` cannot display it: React renders an unmatched value as the
     * first option, which would show a DIFFERENT answer than the one stored and
     * then save it on the next submit. The drawer still shows the raw stored
     * value, so nothing is hidden from the reader — only from the editor.
     */
    return '';
  }
  return s;
}

/** Keys this list replaced. The drawer renders them so old answers stay
 *  visible; nothing writes them again. */
export const LEGACY_AMENITY_KEYS: Record<string, string> = {
  breakfast_included: 'breakfast_type',
  parking_included: 'parking_type / parking_cost / parking_one_car_included',
};

/** Reader-facing label for a stored key, including ones no longer asked. */
export function amenityLabel(key: string): string {
  const q = AMENITY_QUESTIONS.find((x) => x.key === key);
  if (q) return q.label;
  // Unknown or retired key: humanise it rather than showing snake_case. These
  // still appear in the drawer for agreements answered before this list.
  return key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** The label for a stored VALUE, so the drawer shows "Buffet" not "B". */
export function amenityValueLabel(key: string, value: unknown): string {
  const q = AMENITY_QUESTIONS.find((x) => x.key === key);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const s = String(value);
  const opt = q?.options?.find((o) => o.value === s);
  return opt ? opt.label : s;
}
